import { NdtInput } from "@npd-ng/network-diagnostic-tool";
import { AppInfo } from "src/config/types";
import { getDomain, getENAEPDomain } from "../util/util";
const logPrefix = "[ADPClient][ConfigServiceHook]";
export type buildJsonType = {
  arch: string;
  platform: string;
  installerName: string;
  installerSizeInBytes: number;
  version: string;
  date: string;
  artifactName: string;
  name: string;
  uninstallGuid: string;
};

export type InstallerType = "msi" | "dmg" | "pkg";

export type Installer = {
  name: string;
  size: string;
  path: string;
  version: string;
  artifactName: string;
  uninstallGuid: string;
};

export type InstallerMap = Record<InstallerType, Installer>;
export type manifestType = {
  manifest: {
    product: {
      name: string;
      version: string;
      buildNumber: number;
      versionColors: string[];
      versionBorderColors: string[];
    };
    app: {
      version: string;
      appCode: string;
      buildDate: string;
      environment: string;
      components: [];
    };
  };
};
export type maintenanceType =
  {
    "status": string,
    "date": string,
    "region": string,
    "scheduledStart": string,
    "maintenance": string,
    "scheduledEnd": string
  };
function getBuildJsonPath() {
  const domain = getDomain();
  const path = domain.includes("localhost")
    ? `${domain}build.json?no-cache-${Date.now()}`
    : `${domain}/download-center/ng-enaep-secure-browser/build.json?no-cache-${Date.now()}`;
  return path;
}
function getManifestPath() {
  const domain = getDomain();
  const path = domain.includes("localhost")
    ? `${domain}naep.manifest.json`
    : `${domain}/adp-web-app/manifest/naep.manifest.json?no-cache-${Date.now()}`;
  return path;
}
function getMaintenancePath() {//TODO implement api call for non localhost
  const domain = getDomain();
  const devURL = "https://enaep.dev27.npdng.org"
  const path = domain.includes("localhost")
    ? `${devURL}/api-student-data-services/v1/student-data-services/download-center/system-status`
    : `${domain}/api-student-data-services/v1/student-data-services/download-center/system-status`;
  return path;
}
function getLocalePath(locale: "en" | "es") {
  const domain = getDomain();
  const resourceLocation = window.location.href;
  const domainPath = resourceLocation.replace(
    "index.html",
    `locale/${locale}.json`
  );
  const path = domain.includes("localhost")
    ? `${domain}locale/${locale}.json`
    : `${domainPath}?no-cache-${Date.now()}`;
  return path;
}
function getInputConfigPath() {
  const domain = getDomain();
  const resourceLocation = window.location.href;
  const domainPath = resourceLocation.replace(
    "index.html",
    `config/input.json`
  );
  const path = domain.includes("localhost")
    ? `${domain}/config/input.json`
    : `${domainPath}?no-cache-${Date.now()}`;
  return path;
}
const convertSize = (bytes: number) => {
  return `(${(bytes / 1048576).toFixed(2)}MB)`;
};

const installerConfig: Record<InstallerType, { dir: string; buildJson: string }> = {
  msi: { dir: "msi/", buildJson: "build.json" },
  dmg: { dir: "", buildJson: "build-dmg.json" },
  pkg: { dir: "", buildJson: "build-pkg.json" },
};

function getBuildJsonUrl(type: InstallerType) {
  const domain = getDomain();
  const { dir, buildJson } = installerConfig[type];
  const relative = `${dir}${buildJson}`;
  return domain.includes("localhost")
    ? `${domain}${relative}?no-cache-${Date.now()}`
    : `${domain}/download-center/ng-enaep-secure-browser/${relative}?no-cache-${Date.now()}`;
}

function getInstallerUrl(type: InstallerType, installerName: string) {
  const domain = getENAEPDomain();
  const { dir } = installerConfig[type];
  return `${domain}/download-center/ng-enaep-secure-browser/${dir}${installerName}`;
}

export async function getInstallerInfo(type: InstallerType): Promise<Installer> {
  console.log(logPrefix, `fetching ${type} installer info...`);
  const buildInfo: buildJsonType = await fetchData(getBuildJsonUrl(type));
  return {
    name: buildInfo?.installerName ?? "",
    size: convertSize(buildInfo?.installerSizeInBytes) || convertSize(0),
    path: getInstallerUrl(type, buildInfo?.installerName),
    version: buildInfo?.version ?? "",
    artifactName: buildInfo?.artifactName ?? "",
    uninstallGuid: buildInfo?.uninstallGuid ?? "",
  };
}

export async function getAllInstallers(): Promise<InstallerMap> {
  const [msi, dmg, pkg] = await Promise.all([
    getInstallerInfo("msi"),
    getInstallerInfo("dmg"),
    getInstallerInfo("pkg"),
  ]);
  return { msi, dmg, pkg };
}

async function fetchData(url: string) {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error("Network response was not ok " + response.statusText);
    }
    const data = await response.json(); // Parse the JSON from the response
    console.log({ data }); // Handle the JSON data
    return data;
  } catch (error) {
    console.error("There was a problem with the fetch operation:", error);
  }
}
export async function getMaintenanceStatus(url: string,cb:()=>void) {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`The maintenance response call was not ok:${response.statusText}`);
    }
    const data = await response.json(); // Parse the JSON from the response
    console.log({ data }); // Handle the JSON data
    return data;
  } catch (error) {
    console.error("There was a problem with the maintenance call:", error);
    cb();
  }
}

export async function getBuildInfo(): Promise<buildJsonType> {
  console.log(logPrefix, "fetching buildInfo...");
  return await fetchData(getBuildJsonPath()).then((response) => {
    console.log(logPrefix, "buildInfo fetch status", response.status);
    return response as buildJsonType;
  });
}
export async function getManifestInfo(): Promise<manifestType> {
  console.log(logPrefix, "fetching manifest...");
  return await fetchData(getManifestPath()).then((response) => {
    console.log(logPrefix, "manifest fetch status", response.status);
    return response as manifestType;
  });
}
export async function getMaintenance(cb:()=>void): Promise<maintenanceType> {
  console.log(logPrefix, "fetching maintenance...");
  return await getMaintenanceStatus(getMaintenancePath(),cb).then((response) => {
    console.log(logPrefix, "maintenance fetch status", response.status);
    return response as maintenanceType;
  });
}
export async function getLocale(): Promise<{ es: AppInfo; en: AppInfo }> {
  console.log(logPrefix, "fetching locale...");
  return await fetchData(getLocalePath("en")).then(async (response) => {
    console.log(logPrefix, "manifest fetch status", response.status);
    const es = await fetchData(getLocalePath("es"));
    return { en: response, es: es };
  });
}

export async function getInputConfig(): Promise<NdtInput> {
  console.log(logPrefix, "fetching Input Config...");
  return await fetchData(getInputConfigPath()).then(async (response) => {
    console.log(logPrefix, "input config fetch status", response.status);
    response.nonProd.ndtConfiguration.contentFiltering.urls.forEach((request:any)=>{
      const urlPostfix = request.url
      request.url = `${getENAEPDomain()}${urlPostfix}`
    })
    response.prod.ndtConfiguration.contentFiltering.urls.forEach((request:any)=>{
      const urlPostfix = request.url
      request.url = `${getENAEPDomain()}${urlPostfix}`
    })
    return response as NdtInput;
  });
}
